import { useState } from "react";
import { Highlight, themes } from "prism-react-renderer";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { CopyButton } from "~/components/assistant/CopyButton";
import { DebugField } from "~/components/assistant/messages/DebugField";
import { DebugSection } from "~/components/assistant/messages/DebugSection";
import { LineageTab } from "~/components/assistant/messages/LineageTab";
import { Dataframe } from "~/components/assistant/types";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { trackEvent } from "~/utils/monitoring";

interface DataframeMessageProps {
  dataframe: Dataframe;
  debugMode?: boolean;
}

// Table component for rendering dataframe data in pandas "split" orient format
function DataTable({
  data,
}: {
  data: { columns: string[]; index: (string | number)[]; data: any[][] };
}) {
  const { columns, data: rows } = data;

  return (
    <div className="overflow-x-auto bg-white">
      <table className="min-w-full border border-gray-200">
        <thead className="bg-gray-100">
          <tr>
            {columns.map((column) => (
              <th
                key={column}
                className="px-3 py-2 text-left text-xs font-medium text-gray-900 border-r border-gray-200 last:border-r-0 text-nowrap"
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b border-gray-200 last:border-b-0">
              {row.map((cell, colIndex) => (
                <td
                  key={`${columns[colIndex]}-${rowIndex}`}
                  className="px-3 py-2 text-sm text-gray-700 border-r border-gray-200 last:border-r-0"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type TabType = "table" | "code" | "lineage";

interface Tab {
  id: TabType;
  label: string;
}

// DataframeMessage component with tabbed interface
export function DataframeMessage({ dataframe, debugMode = false }: DataframeMessageProps) {
  const [activeTab, setActiveTab] = useState<TabType>("table");
  const { conversationId, contextId } = useAssistantContext();

  // Load Python language support for syntax highlighting
  const { isLoading, Prism: PrismInstance } = usePrismLanguages(["python"]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);

    if (contextId) {
      trackEvent("analysis_agent_artifact_tab_viewed", {
        dataset_id: contextId,
        conversation_id: conversationId || undefined,
        artifact_id: dataframe.artifact_id || undefined,
        artifact_type: "dataframe",
        context: "dataset",
        tab,
      });
    }
  };

  const tabs: Tab[] = [{ id: "table", label: "Table" }];
  if (dataframe?.code) {
    tabs.push({ id: "code", label: "Code" });
  }

  if (dataframe?.artifact_id) {
    tabs.push({ id: "lineage", label: "Lineage" });
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 pt-1 w-full">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors select-none cursor-pointer ${
              activeTab === tab.id
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="overflow-x-auto">
        {activeTab === "table" && (
          <div>
            <DataTable data={dataframe.content.data} />
            {dataframe?.summary && (
              <div className="mt-3 text-xs text-gray-600">{dataframe.summary}</div>
            )}
          </div>
        )}

        {activeTab === "code" && dataframe?.code && (
          <>
            {/* Code Header with Copy Button */}
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-medium text-gray-700">Python Code</h4>
              <CopyButton text={dataframe.code} variant="light" size="sm" />
            </div>
            <div className="h-full overflow-hidden rounded-sm">
              {!isLoading && (
                <Highlight
                  theme={themes.dracula}
                  code={dataframe.code}
                  language="python"
                  prism={PrismInstance as any}
                >
                  {({ style, tokens, getLineProps, getTokenProps, className }) => (
                    <pre
                      className={`not-prose h-full p-4 overflow-x-auto text-xs font-mono leading-relaxed ${
                        className || ""
                      }`}
                      style={style}
                    >
                      {tokens.map((line, i) => (
                        <div
                          {...getLineProps({ line, key: i })}
                          key={`line-${i}`}
                          style={{ minHeight: "1.5em" }}
                        >
                          {line.map((token, key) => (
                            <span {...getTokenProps({ token, key })} key={`token-${key}`} />
                          ))}
                        </div>
                      ))}
                    </pre>
                  )}
                </Highlight>
              )}
              {isLoading && (
                <div className="h-full p-4 bg-gray-800 text-gray-300 text-sm font-mono leading-relaxed">
                  Loading syntax highlighting...
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === "lineage" && dataframe?.artifact_id && (
          <LineageTab artifactId={dataframe.artifact_id} />
        )}
      </div>

      {/* Debug Section */}
      {debugMode && (
        <div className="mt-3">
          <DebugSection
            content={
              <div className="space-y-3 pt-2 pb-4 px-4 rounded-lg border border-gray-200 text-gray-800">
                <DebugField label="Dataframe Data" content={dataframe} />
              </div>
            }
            defaultExpanded={false}
          />
        </div>
      )}
    </div>
  );
}
